import { Dispatch, SetStateAction } from "react";

import { Column, Row, Text } from "@bridgesplit/ui";
import { AppButton, BalanceDisplay, GetAssetButton } from "app/components/common";
import { TokenInputWithPrice } from "app/components/common/util";
import { AbfLoanExpanded, getZcCollateral, getZcLedger } from "@bridgesplit/abf-react";

import { useManualDecreaseLeverage } from "./useManualDecreaseLeverage";
import { LoanPositionStats } from "./LoanPositionStats";

export function ManualDecreaseLeverage({
    loanExpanded,
    setIsSwapOpen
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    setIsSwapOpen?: Dispatch<SetStateAction<boolean>>;
}) {
    const c = useManualDecreaseLeverage({ loanExpanded });
    const collateral = getZcCollateral(loanExpanded);
    const collateralMetadata = collateral?.metadata;
    const principalMetadata = loanExpanded?.principalMetadata;

    const ledger = getZcLedger(loanExpanded);
    const currentDebt = ledger?.ledgerDebt.total ?? 0;
    const currentCollateral = collateral?.amount ?? 0;
    const repayAmount = c.repay.value ?? 0;
    const withdrawAmount = c.withdraw.value ?? 0;
    const newPrincipalAmount = Math.max(0, currentDebt - repayAmount);
    const newCollateralAmount = Math.max(0, currentCollateral - withdrawAmount);

    return (
        <Column spacing={3}>
            <Column spacing={3}>
                <Column spacing={0.5}>
                    <Row spaceBetween>
                        <Column>
                            <Text variant="body2" color="caption">
                                Repay Principal
                            </Text>
                        </Column>
                        <GetAssetButton
                            buyToken={principalMetadata}
                            sellToken={collateralMetadata}
                            swapOnClick={!setIsSwapOpen ? undefined : () => setIsSwapOpen(true)}
                        />
                    </Row>
                    <TokenInputWithPrice
                        value={c.repay.value}
                        metadata={c.repay.metadata}
                        selectToken={null}
                        setValue={c.repay.setValue}
                        hideUsdPrice={false}
                        belowInput={
                            <BalanceDisplay
                                isLoading={c.repay.balanceLoading}
                                balance={c.repay.balance}
                                metadata={c.repay.metadata}
                                setMax={c.repay.setMax}
                                insufficientBalance={c.repay.insufficientBalance}
                            />
                        }
                    />
                </Column>

                <Column spacing={0.5}>
                    <Text variant="body2" color="caption">
                        Withdraw Collateral
                    </Text>
                    <TokenInputWithPrice
                        loading={false}
                        metadata={c.withdraw.metadata}
                        value={c.withdraw.value}
                        setValue={c.withdraw.setValue}
                        selectToken={null}
                        belowInput={
                            <BalanceDisplay
                                isLoading={false}
                                balance={c.withdraw.balance}
                                metadata={c.withdraw.metadata}
                                setMax={c.withdraw.setMax}
                                insufficientBalance={false}
                                maxButtonCta="Max"
                                maxText="Available"
                            />
                        }
                    />
                </Column>
            </Column>

            <LoanPositionStats
                loanExpanded={loanExpanded}
                loopPosition={undefined}
                newPrincipalAmount={newPrincipalAmount}
                newCollateralAmount={newCollateralAmount}
                isFullRepay={c.repay.isFullRepay}
                loading={c.repay.balanceLoading}
                show={{
                    debtAndCollateral: true,
                    health: !c.repay.isFullRepay,
                    netApyCollapsable: !c.repay.isFullRepay,
                    executionDetails: false
                }}
            />
            <AppButton isTransaction fullWidth {...c.buttonProps} />
        </Column>
    );
}
